import { useEffect, useState } from 'react'
import { api } from './api.js'
import Dialog from './Dialog.jsx'
import Icon from './Icon.jsx'
import { euro } from './format.js'

/**
 * Dialog für eine Gruppe von Einstellungen.
 *
 * Die Felder baut die Komponente aus dem Verzeichnis, das der Server
 * mitschickt — eine neue Einstellung im Backend erscheint hier von selbst,
 * ohne dass an dieser Datei etwas zu ändern wäre.
 */
export default function Einstellungsgruppe({ gruppe, verzeichnis, mail, beiGespeichert, beiSchliessen }) {
  const felder = verzeichnis.filter((e) => e.gruppe === gruppe)

  const [entwurf, setEntwurf] = useState({})
  const [fehler, setFehler] = useState('')
  const [meldung, setMeldung] = useState('')
  const [speichert, setSpeichert] = useState(false)

  const offen = Object.keys(entwurf).length > 0

  function aendere(schluessel, wert) {
    setEntwurf((bisher) => ({ ...bisher, [schluessel]: wert }))
    setMeldung('')
  }

  function wertVon(eintrag) {
    return eintrag.schluessel in entwurf ? entwurf[eintrag.schluessel] : eintrag.wert
  }

  async function speichern() {
    setSpeichert(true)
    setFehler('')
    try {
      const antwort = await api('/einstellungen', { methode: 'PUT', koerper: { werte: entwurf } })
      beiGespeichert(antwort)
      setEntwurf({})
      setMeldung(
        antwort.geaendert === 0
          ? 'Es gab nichts zu ändern.'
          : `${antwort.geaendert} ${antwort.geaendert === 1 ? 'Änderung' : 'Änderungen'} gespeichert.`
      )
    } catch (e) {
      setFehler(e.message)
    } finally {
      setSpeichert(false)
    }
  }

  return (
    <Dialog titel={gruppe} beiSchliessen={beiSchliessen}>
      {felder.map((eintrag) => (
        <Feld
          key={eintrag.schluessel}
          eintrag={eintrag}
          wert={wertVon(eintrag)}
          beiAenderung={(w) => aendere(eintrag.schluessel, w)}
        />
      ))}

      {meldung && (
        <div className="meldung ok" role="status">
          <Icon name="haken" groesse={18} />
          <span>{meldung}</span>
        </div>
      )}
      {fehler && (
        <div className="meldung fehler" role="alert">
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}

      <div className="dialog__fuss">
        <button className="knopf knopf--leise" type="button" onClick={beiSchliessen}>
          Schließen
        </button>
        <button
          className="knopf knopf--haupt"
          type="button"
          onClick={speichern}
          disabled={!offen || speichert}
        >
          <Icon name="haken" /> {speichert ? 'Speichert …' : 'Speichern'}
        </button>
      </div>

      {gruppe === 'Mailversand' && <Mailpruefung mail={mail} offeneAenderungen={offen} />}
      {gruppe === 'Minijob' && <Verdienstgrenze />}
      {gruppe === 'Erinnerung' && <Erinnerungsstand />}
    </Dialog>
  )
}

// --- Ein Feld je Art --------------------------------------------------------

function Feld({ eintrag, wert, beiAenderung }) {
  const kennung = `feld-${eintrag.schluessel}`

  if (eintrag.art === 'schalter') {
    const an = wert === true
    return (
      <div className="schalterfeld">
        <button
          id={kennung}
          type="button"
          className="schalter"
          role="switch"
          aria-checked={an}
          aria-labelledby={`${kennung}-text`}
          onClick={() => beiAenderung(!an)}
        />
        <div>
          <label className="schalterfeld__text" id={`${kennung}-text`} htmlFor={kennung}>
            {eintrag.beschriftung}
          </label>
          {eintrag.hilfe && <p className="hinweis">{eintrag.hilfe}</p>}
        </div>
      </div>
    )
  }

  if (eintrag.art === 'geheim') {
    return (
      <div className="feld">
        <label htmlFor={kennung}>{eintrag.beschriftung}</label>
        <input
          id={kennung}
          type="password"
          autoComplete="new-password"
          value={wert ?? ''}
          placeholder={eintrag.gesetzt ? '•••••••• hinterlegt' : 'nicht hinterlegt'}
          onChange={(e) => beiAenderung(e.target.value)}
        />
        <p className="hinweis">
          {eintrag.hilfe}
          {eintrag.gesetzt && ' Leer lassen behält das gespeicherte Passwort.'}
        </p>
      </div>
    )
  }

  if (eintrag.art === 'auswahl') {
    return (
      <div className="feld">
        <label htmlFor={kennung}>{eintrag.beschriftung}</label>
        <select id={kennung} value={String(wert ?? '')} onChange={(e) => beiAenderung(e.target.value)}>
          {eintrag.optionen.map((o) => (
            <option key={o.wert} value={o.wert}>
              {o.text}
            </option>
          ))}
        </select>
        {eintrag.hilfe && <p className="hinweis">{eintrag.hilfe}</p>}
      </div>
    )
  }

  if (eintrag.art === 'mehrzeilig') {
    return (
      <div className="feld">
        <label htmlFor={kennung}>{eintrag.beschriftung}</label>
        <textarea id={kennung} rows={3} value={wert ?? ''} onChange={(e) => beiAenderung(e.target.value)} />
        {eintrag.hilfe && <p className="hinweis">{eintrag.hilfe}</p>}
      </div>
    )
  }

  if (eintrag.art === 'zahl') {
    return (
      <div className="feld feld--zahl">
        <label htmlFor={kennung}>{eintrag.beschriftung}</label>
        <input
          id={kennung}
          type="number"
          inputMode="numeric"
          min={eintrag.min}
          max={eintrag.max}
          value={wert ?? ''}
          onChange={(e) => beiAenderung(e.target.value === '' ? '' : Number(e.target.value))}
        />
        {eintrag.hilfe && <p className="hinweis">{eintrag.hilfe}</p>}
      </div>
    )
  }

  return (
    <div className="feld">
      <label htmlFor={kennung}>{eintrag.beschriftung}</label>
      <input id={kennung} type="text" value={wert ?? ''} onChange={(e) => beiAenderung(e.target.value)} />
      {eintrag.hilfe && <p className="hinweis">{eintrag.hilfe}</p>}
    </div>
  )
}

// --- Mailprüfung ------------------------------------------------------------

function Mailpruefung({ mail, offeneAenderungen }) {
  const [an, setAn] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [ergebnis, setErgebnis] = useState(null)

  async function pruefen(mitMail) {
    setLaeuft(true)
    setErgebnis(null)
    try {
      const antwort = await api('/einstellungen/mailtest', {
        methode: 'POST',
        koerper: { an: mitMail ? an : '' },
      })
      setErgebnis({ gut: true, text: antwort.meldung })
    } catch (e) {
      setErgebnis({ gut: false, text: e.message })
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="pruefblock">
      <h3>Verbindung prüfen</h3>

      {offeneAenderungen && (
        <div className="meldung warnung">
          <Icon name="info" groesse={18} />
          <span>Erst speichern — geprüft werden die gespeicherten Werte.</span>
        </div>
      )}

      <p className="hinweis hinweis--oben">
        {mail?.bereit
          ? `Versandbereit als ${mail.absender}.`
          : mail?.fehlt?.length
            ? `Es fehlen noch: ${mail.fehlt.join(', ')}.`
            : 'Der Versand ist ausgeschaltet.'}
      </p>

      <div className="feld">
        <label htmlFor="testmail">Testmail an</label>
        <input
          id="testmail"
          type="email"
          value={an}
          placeholder="eigene Adresse"
          onChange={(e) => setAn(e.target.value)}
        />
      </div>

      <div className="knopfreihe">
        <button
          className="knopf knopf--leise knopf--klein"
          type="button"
          disabled={laeuft}
          onClick={() => pruefen(false)}
        >
          <Icon name="schloss" groesse={16} /> Nur Verbindung
        </button>
        <button
          className="knopf knopf--leise knopf--klein"
          type="button"
          disabled={laeuft || !an}
          onClick={() => pruefen(true)}
        >
          <Icon name="senden" groesse={16} /> {laeuft ? 'Prüft …' : 'Testmail senden'}
        </button>
      </div>

      {ergebnis && (
        <div className={`meldung ${ergebnis.gut ? 'ok' : 'fehler'}`} style={{ marginTop: '.75rem', marginBottom: 0 }}>
          <Icon name={ergebnis.gut ? 'haken' : 'warnung'} groesse={18} />
          <span>{ergebnis.text}</span>
        </div>
      )}
    </div>
  )
}

// --- Verdienstgrenze (eigene Tabelle, weil sie je Jahr gilt) -----------------

function Verdienstgrenze() {
  const jahr = new Date().getFullYear()
  const [grenze, setGrenze] = useState(null)
  const [text, setText] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState('')
  const [fehler, setFehler] = useState('')

  // Die Grenze hängt am Jahr und liegt in einer eigenen Tabelle — sie kommt
  // deshalb nicht über das Einstellungsverzeichnis, sondern eigens.
  useEffect(() => {
    let abgebrochen = false
    api(`/uebersicht/${jahr}/1`)
      .then((d) => {
        if (abgebrochen) return
        setGrenze(d.grenze)
        if (d.grenze?.betragCent) setText((d.grenze.betragCent / 100).toFixed(2).replace('.', ','))
      })
      .catch(() => {
        if (!abgebrochen) setGrenze(null)
      })
    return () => {
      abgebrochen = true
    }
  }, [jahr])

  async function speichern(ereignis) {
    ereignis.preventDefault()
    const bereinigt = text.replace(',', '.').trim()
    if (!/^\d+(\.\d{1,2})?$/.test(bereinigt)) return setFehler('Bitte einen Betrag wie 603,00 eingeben.')
    const cent = Math.round(Number(bereinigt) * 100)

    setLaeuft(true)
    setFehler('')
    try {
      await api(`/verdienstgrenze/${jahr}`, { methode: 'PUT', koerper: { betragCent: cent } })
      setGrenze({ betragCent: cent, bestaetigt: true })
      setMeldung('Gespeichert und als bestätigt vermerkt.')
    } catch (e) {
      setFehler(e.message)
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="pruefblock">
      <h3>Verdienstgrenze {jahr}</h3>
      <p className="hinweis hinweis--oben">
        Der hinterlegte Startwert stammt aus der üblichen Formel und sollte auf den amtlichen Wert
        geprüft werden. Gilt je Kalenderjahr.
      </p>

      <form onSubmit={speichern}>
        <div className="feld feld--zahl">
          <label htmlFor="grenzbetrag">Betrag in Euro</label>
          <input
            id="grenzbetrag"
            type="text"
            inputMode="decimal"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="0,00"
          />
        </div>
        <button className="knopf knopf--leise knopf--klein" type="submit" disabled={laeuft}>
          <Icon name="haken" groesse={16} /> Grenze speichern
        </button>
      </form>

      {grenze && (
        <p className="hinweis">
          Hinterlegt: {euro(grenze.betragCent)}
          {grenze.bestaetigt ? ' (bestätigt)' : ' — noch nicht bestätigt'}
        </p>
      )}
      {meldung && (
        <div className="meldung ok" style={{ marginTop: '.75rem', marginBottom: 0 }}>
          <Icon name="haken" groesse={18} />
          <span>{meldung}</span>
        </div>
      )}
      {fehler && (
        <div className="meldung fehler" style={{ marginTop: '.75rem', marginBottom: 0 }}>
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}
    </div>
  )
}

// --- Was gerade auf Freigabe wartet -----------------------------------------

function Erinnerungsstand() {
  const [stand, setStand] = useState(null)
  const [laeuft, setLaeuft] = useState(false)
  const [ergebnis, setErgebnis] = useState(null)

  useEffect(() => {
    let abgebrochen = false
    api('/erinnerung')
      .then((d) => !abgebrochen && setStand(d))
      .catch(() => !abgebrochen && setStand(null))
    return () => {
      abgebrochen = true
    }
  }, [])

  async function jetztSenden() {
    setLaeuft(true)
    setErgebnis(null)
    try {
      const antwort = await api('/erinnerung', { methode: 'POST' })
      setErgebnis({ gut: true, text: `Erinnerung an ${antwort.an} verschickt.` })
    } catch (e) {
      setErgebnis({ gut: false, text: e.message })
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="pruefblock">
      <h3>Was gerade offen ist</h3>

      {!stand && <p className="hinweis hinweis--oben">Einen Moment …</p>}

      {stand && stand.wartend.length === 0 && (
        <p className="hinweis hinweis--oben">
          Nichts wartet auf Freigabe — es würde also keine Mail verschickt.
        </p>
      )}

      {stand && stand.wartend.length > 0 && (
        <>
          <p className="hinweis hinweis--oben">
            {stand.wartend.length === 1
              ? 'Ein Monatsblatt wartet auf Freigabe:'
              : `${stand.wartend.length} Monatsblätter warten auf Freigabe:`}
          </p>
          <div className="summenkarte">
            {stand.wartend.map((w, i) => (
              <div className="summenzeile" key={`${w.name}-${w.monat}-${i}`}>
                <span>
                  {w.name} <span className="gedaempft">· {w.monat}</span>
                </span>
                <strong>{w.dauer}</strong>
              </div>
            ))}
          </div>
        </>
      )}

      {stand && stand.nichtEingereicht.length > 0 && (
        <p className="hinweis">
          Noch nicht eingereicht ({stand.monat}):{' '}
          {stand.nichtEingereicht.map((n) => `${n.name} (${n.dauer})`).join(' · ')}
        </p>
      )}

      <div className="knopfreihe" style={{ marginTop: '.75rem' }}>
        <button
          className="knopf knopf--leise knopf--klein"
          type="button"
          disabled={laeuft || !stand || stand.wartend.length === 0}
          onClick={jetztSenden}
        >
          <Icon name="senden" groesse={16} /> {laeuft ? 'Sendet …' : 'Jetzt senden'}
        </button>
      </div>

      {ergebnis && (
        <div
          className={`meldung ${ergebnis.gut ? 'ok' : 'fehler'}`}
          style={{ marginTop: '.75rem', marginBottom: 0 }}
        >
          <Icon name={ergebnis.gut ? 'haken' : 'warnung'} groesse={18} />
          <span>{ergebnis.text}</span>
        </div>
      )}
    </div>
  )
}
